import { Component, Input, OnInit, OnChanges, SimpleChanges, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-icon',
  standalone: true,
  imports: [],
  template: `
    <span class="icon-container" 
          [style.width.px]="size" 
          [style.height.px]="size" 
          [class]="className"
          [innerHTML]="safeSvg">
    </span>
  `,
  styles: [`
    :host { display: inline-flex; align-items: center; justify-content: center; vertical-align: middle; }
    .icon-container { display: inline-flex; align-items: center; justify-content: center; }
    ::ng-deep .icon-container svg { width: 100%; height: 100%; fill: currentColor; display: block; }
  `]
})
export class IconComponent implements OnInit, OnChanges {
  private http = inject(HttpClient);
  private sanitizer = inject(DomSanitizer);
  private platformId = inject(PLATFORM_ID);

  @Input() name = '';
  @Input() size: string | number = 24;
  @Input() className = '';

  safeSvg: SafeHtml | null = null;
  private static iconCache = new Map<string, string>();

  ngOnInit() {
    this.loadIcon();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['name'] && !changes['name'].firstChange) {
      this.loadIcon();
    }
  }

  private loadIcon() {
    if (!this.name) return;

    const fileName = this.mapIconName(this.name);

    if (IconComponent.iconCache.has(fileName)) {
      this.setSvg(IconComponent.iconCache.get(fileName)!);
      return;
    }

    // Only make HTTP requests in the browser
    if (isPlatformBrowser(this.platformId)) {
      this.http.get(`/assets/icons/${fileName}.svg`, { responseType: 'text' })
        .pipe(take(1))
        .subscribe({
          next: (svg) => {
            if (svg.trim().startsWith('<svg')) {
              IconComponent.iconCache.set(fileName, svg);
              this.setSvg(svg);
            } else {
              console.error(`Invalid SVG content for icon: ${fileName}`);
            }
          },
          error: (err) => {
            console.error(`Icon ${fileName} not found in /assets/icons/`, err);
          }
        });
    } else {
      // Server-side rendering: we can't load icons via HTTP
      // Icons will be loaded on the client side after hydration
      // No warning needed as this is expected behavior
    }
  }

  private setSvg(svg: string) {
    this.safeSvg = this.sanitizer.bypassSecurityTrustHtml(svg);
  }

  private mapIconName(name: string): string {
    const mapping: Record<string, string> = {
      'tools': 'screwdriver-wrench',
      'map-marker-alt': 'location-dot',
      'home': 'house',
      'columns': 'table-columns',
      'th': 'table-cells',
      'thermometer-half': 'temperature-half',
      'facebook-f': 'facebook-f',
      'whatsapp': 'whatsapp'
    };
    // Force reload comment
    return mapping[name] || name;
  }
}
